import { BaseRepository } from '@/base/base-repository';

export interface QuestDescription {
	key: string;
	title: string;
	description: string;
	points?: number;
	timePeriod?: 'ALL_TIME' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
	goal?: number;
}

export type QuestClaimStatus = 'in_progress' | 'ready_to_claim' | 'claimed' | 'external' | 'upcoming' | 'expired';

export interface QuestStatusResult {
	isValid: boolean;
	validatedAt?: string | null;
	points?: number;
	progress?: number;
	goal?: number;
	claimStatus?: QuestClaimStatus;
	claimedAt?: string | null;
	claimable?: boolean;
	managedExternally?: boolean;
	state?: string;
	timePeriod?: 'ALL_TIME' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
	period?: {
		type: 'ALL_TIME' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
		periodStart: string;
		periodEnd: string | null;
		effectiveStart: string;
		effectiveEnd: string | null;
	};
}

export type QuestClaimRecord = {
	id: number;
	questKey: string;
	questTitle: string;
	questDescription: string | null;
	xpAmount: number;
	status: 'PENDING' | 'CLAIMED' | 'REVOKED';
	timePeriod: 'ALL_TIME' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
	periodStart: string;
	periodEnd: string | null;
	claimedAt: string | null;
};

export type QuestClaimStreak = {
	currentDays: number;
	claimedToday: boolean;
	anchorDate: string | null;
	lastClaimedAt: string | null;
	nextMilestone: number | null;
	remainingToNextMilestone: number | null;
};

export type QuestClaimHistoryPage = {
	totalXpClaimed: number;
	totalClaims: number;
	nextCursor: number | null;
	claimStreak: QuestClaimStreak;
	items: QuestClaimRecord[];
};

class QuestRepository extends BaseRepository {
	async getAllQuests(gameType?: string): Promise<QuestDescription[]> {
		const response = await this.httpClient({
			method: 'get',
			url: '/quests/list',
			params: gameType ? { game: gameType } : undefined,
		});

		// Deduplicate quests by key to prevent duplicates
		const questMap = new Map<string, QuestDescription>();
		response?.forEach((quest: QuestDescription) => {
			questMap.set(quest.key, quest);
		});

		return Array.from(questMap.values());
	}

	async checkQuestStatus(
		campaignSlug: string,
		questKey: string,
		address: string,
	): Promise<{
		result: QuestStatusResult;
	}> {
		const response = await this.httpClient({
			method: 'get',
			url: `/quests/campaigns/${campaignSlug}/${questKey}/status`,
			params: { address },
		});

		// Ensure we have a proper response structure
		if (!response) {
			throw new Error(`No response received for quest ${questKey}`);
		}

		const rawResult: QuestStatusResult | undefined = response.result;
		if (!rawResult) {
			return {
				result: {
					isValid: response.isValid || false,
					validatedAt: response.validatedAt,
					progress: response.progress || 0,
					goal: response.goal || 1,
					points: response.points || 100,
					claimStatus: (response.claimStatus || 'in_progress') as QuestClaimStatus,
					claimedAt: response.claimedAt,
					claimable: response.claimable ?? true,
					managedExternally: response.managedExternally ?? false,
					state: response.state,
					period: response.period,
				},
			};
		}

		return {
			result: {
				isValid: rawResult.isValid ?? false,
				validatedAt: rawResult.validatedAt,
				progress: rawResult.progress ?? 0,
				goal: rawResult.goal ?? 1,
				points: rawResult.points ?? 100,
				claimStatus: (rawResult.claimStatus || 'in_progress') as QuestClaimStatus,
				claimedAt: rawResult.claimedAt,
				claimable: rawResult.claimable ?? true,
				managedExternally: rawResult.managedExternally ?? false,
				state: rawResult.state,
				timePeriod: rawResult.timePeriod ?? rawResult.period?.type,
				period: rawResult.period,
			},
		};
	}

	async checkQuestStatuses(
		campaignSlug: string,
		address: string,
		quests: Array<{ key: string; mode?: string }>,
	): Promise<Record<string, QuestStatusResult>> {
		const response = await this.httpClient<{ results: Record<string, QuestStatusResult> }>({
			method: 'post',
			url: `/quests/campaigns/${campaignSlug}/status`,
			data: {
				address,
				quests: quests.map((quest) => ({ key: quest.key, mode: quest.mode })),
			},
		});

		return response?.results ?? {};
	}

	async claimQuest(
		campaignSlug: string,
		questKey: string,
		address: string,
	): Promise<{
		success: boolean;
		xpAmount?: number;
		claimId?: number;
		error?: string;
	}> {
		return this.httpClient({
			method: 'post',
			url: `/quests/campaigns/${campaignSlug}/${questKey}/claim`,
			data: { address },
		});
	}

	async getClaimHistory(
		campaignSlug: string,
		address: string,
		params?: { limit?: number; cursor?: number | null },
	): Promise<QuestClaimHistoryPage> {
		return this.httpClient({
			method: 'get',
			url: `/quests/campaigns/${campaignSlug}/claims`,
			params: {
				address,
				...(typeof params?.limit === 'number' ? { limit: params.limit } : {}),
				...(typeof params?.cursor === 'number' ? { cursor: params.cursor } : {}),
			},
		});
	}
}

export const questRepository = new QuestRepository();
